import React from 'react';
import { ArrowRight, Zap, Heart, Leaf, ChevronDown } from 'lucide-react';

const BRAND = {
  turquoise: '#81CBD6',
  pink: '#F39DB2',
  yellow: '#FFD16F',
};

export const HeroSection = ({ scrollToConfigurator, configuratorRef }) => {
  return (
    <section 
      className="relative overflow-hidden min-h-screen flex flex-col" 
      style={{ backgroundColor: '#0a0a0a', minHeight: '100svh' }}
    >
      {/* Background Image with Glow */}
      <div className="absolute inset-0">
        <img 
          src="https://customer-assets.emergentagent.com/job_ffcba6fb-f7d3-44a0-a7e0-f1289db8a6b5/artifacts/n75jxvwp_FancyDonutsPM-2%203.png"
          alt="Fancy Donuts"
          className="w-full h-full object-cover"
          style={{ 
            objectPosition: 'center 30%', 
            opacity: 0.4,
            filter: 'contrast(1.1) saturate(1.1) brightness(0.9)'
          }}
        />
        <div 
          className="absolute inset-0" 
          style={{ background: 'linear-gradient(180deg, rgba(10,10,10,0.2) 0%, rgba(10,10,10,0.7) 60%, rgba(10,10,10,1) 100%)' }} 
        />
      </div>
      
      {/* Subtle Glow Effect */}
      <div 
        className="absolute top-1/3 left-1/2 -translate-x-1/2 w-[500px] h-[300px] rounded-full blur-[150px] opacity-15" 
        style={{ background: `linear-gradient(135deg, ${BRAND.turquoise}, ${BRAND.pink})` }} 
      />

      {/* Hero Content */}
      <div className="relative z-10 flex flex-col items-center text-center px-6 pt-[14vh] sm:pt-[10vh] flex-grow">
        {/* Premium Badge with Shimmer Animation */}
        <div 
          className="mb-4 px-5 py-2 rounded-full text-[11px] font-semibold tracking-widest uppercase animate-fade-in relative overflow-hidden"
          style={{ 
            background: 'rgba(255,255,255,0.1)',
            border: '1px solid rgba(255,255,255,0.2)',
            color: BRAND.yellow,
            animationDuration: '1s'
          }}
        >
          {/* Shimmer Effect - leuchtender Schein der langsam hin und her wandert */}
          <div 
            className="absolute inset-0 -translate-x-full"
            style={{
              background: 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.15) 40%, rgba(255,255,255,0.4) 50%, rgba(255,255,255,0.15) 60%, transparent 100%)',
              animation: 'shimmer 6s cubic-bezier(0.4, 0, 0.2, 1) infinite'
            }}
          />
          <span className="relative z-10">✨ PREMIUM DONUTS</span>
        </div>
        
        {/* Shimmer keyframes - langsam und smooth */}
        <style>{`
          @keyframes shimmer {
            0% { transform: translateX(-100%); opacity: 0; }
            10% { opacity: 1; }
            90% { opacity: 1; }
            100% { transform: translateX(100%); opacity: 0; }
          }
        `}</style>
        
        {/* Headline */}
        <h1 className="font-black leading-[1.0] mb-4 sm:mb-5">
          <span 
            className="block text-[3.5rem] sm:text-8xl md:text-9xl bg-clip-text text-transparent whitespace-nowrap" 
            style={{ 
              backgroundImage: `linear-gradient(135deg, ${BRAND.turquoise}, ${BRAND.pink})`,
              WebkitBackgroundClip: 'text'
            }}
          >
            Donut-Abo
          </span>
          <span className="block mt-2">
            <span 
              className="text-2xl sm:text-4xl md:text-5xl text-white font-black tracking-tight mr-2" 
              style={{ letterSpacing: '-0.02em' }}
            >
              ODER
            </span>
            <span 
              className="text-[3.5rem] sm:text-8xl md:text-9xl bg-clip-text text-transparent whitespace-nowrap" 
              style={{ 
                backgroundImage: `linear-gradient(135deg, ${BRAND.pink}, ${BRAND.yellow})`,
                WebkitBackgroundClip: 'text'
              }}
            >
              Catering
            </span>
          </span>
        </h1>
        
        {/* Subtext */}
        <p 
          className="text-sm sm:text-base mb-8 sm:mb-10 max-w-md mx-auto font-light" 
          style={{ color: 'rgba(255,255,255,0.5)' }}
        >
          Flexibles Abo für Firmen oder buche dein Catering!
        </p>
        
        {/* CTA Buttons with Float Animation */}
        <div className="flex flex-row items-center justify-center gap-6 sm:gap-8 w-full max-w-xl px-4 mb-10 sm:mb-12">
          <button 
            onClick={() => scrollToConfigurator('abo')} 
            className="animate-float flex items-center justify-center gap-1.5 px-4 sm:px-6 py-4 sm:py-4 rounded-xl font-bold text-xs sm:text-sm text-white transition-all duration-300 active:scale-95 active:brightness-110 whitespace-nowrap hover:animate-none"
            style={{ background: `linear-gradient(135deg, ${BRAND.turquoise}, ${BRAND.pink})` }}
            data-testid="hero-abo-btn"
          >
            <span className="text-sm">🍩</span>
            <span>Donut-Abo starten</span>
            <ArrowRight className="w-3.5 h-3.5 hidden sm:block" />
          </button>
          
          <button 
            onClick={() => scrollToConfigurator('catering')} 
            className="animate-float-delayed flex items-center justify-center gap-1.5 px-4 sm:px-6 py-4 sm:py-4 rounded-xl font-bold text-xs sm:text-sm text-white transition-all duration-300 active:scale-95 active:brightness-110 whitespace-nowrap hover:animate-none"
            style={{ background: `linear-gradient(135deg, ${BRAND.pink}, ${BRAND.yellow})` }}
            data-testid="hero-catering-btn"
          >
            <span className="text-sm">🎉</span>
            <span>Catering anfragen</span>
            <ArrowRight className="w-3.5 h-3.5 hidden sm:block" />
          </button>
        </div>
        
        {/* Trust Badges */}
        <div className="flex items-center justify-center gap-3 mb-6 sm:mb-8">
          <span 
            className="flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[10px]" 
            style={{ backgroundColor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}
          >
            <Zap className="w-3 h-3" style={{ color: BRAND.turquoise }} />
            <span style={{ color: 'rgba(255,255,255,0.5)' }}>Express</span>
          </span>
          <span 
            className="flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[10px]" 
            style={{ backgroundColor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}
          >
            <Heart className="w-3 h-3" style={{ color: BRAND.pink }} />
            <span style={{ color: 'rgba(255,255,255,0.5)' }}>Handgemacht</span>
          </span>
          <span 
            className="flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[10px]" 
            style={{ backgroundColor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}
          >
            <Leaf className="w-3 h-3" style={{ color: '#4ade80' }} />
            <span style={{ color: 'rgba(255,255,255,0.5)' }}>Nachhaltig</span>
          </span>
        </div>
        
        {/* Stats Row */}
        <div className="flex items-center justify-center gap-6 sm:gap-10 mt-6">
          <div className="text-center">
            <div className="text-2xl sm:text-3xl font-black" style={{ color: BRAND.turquoise }}>500+</div>
            <div className="text-[10px] sm:text-xs" style={{ color: 'rgba(255,255,255,0.4)' }}>Zufriedene Kunden</div>
          </div>
          <div className="w-px h-8" style={{ backgroundColor: 'rgba(255,255,255,0.1)' }}></div>
          <div className="text-center">
            <div className="text-2xl sm:text-3xl font-black" style={{ color: BRAND.pink }}>37</div>
            <div className="text-[10px] sm:text-xs" style={{ color: 'rgba(255,255,255,0.4)' }}>Donut-Sorten</div>
          </div>
          <div className="w-px h-8" style={{ backgroundColor: 'rgba(255,255,255,0.1)' }}></div>
          <div className="text-center">
            <div className="text-2xl sm:text-3xl font-black" style={{ color: BRAND.yellow }}>24h</div>
            <div className="text-[10px] sm:text-xs" style={{ color: 'rgba(255,255,255,0.4)' }}>Express-Lieferung</div>
          </div>
        </div>
      </div>
      
      {/* Scroll Hint */}
      <div 
        className="relative z-10 flex justify-center mt-auto" 
        style={{ paddingBottom: 'max(1.5rem, env(safe-area-inset-bottom))' }}
      >
        <button 
          onClick={() => {
            if (configuratorRef?.current) {
              const headerOffset = 140;
              const elementPosition = configuratorRef.current.getBoundingClientRect().top;
              const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
              window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
            }
          }}
          className="flex flex-col items-center gap-1 animate-bounce cursor-pointer group"
          data-testid="scroll-hint-btn"
        >
          <span 
            className="text-[10px] font-medium uppercase tracking-widest" 
            style={{ color: 'rgba(255,255,255,0.3)' }}
          >
            Scroll
          </span>
          <ChevronDown 
            className="w-5 h-5 transition-transform group-hover:translate-y-1" 
            style={{ color: 'rgba(255,255,255,0.3)' }} 
          />
        </button>
      </div>
    </section>
  );
};

export default HeroSection;
